import type { CategoryTotalRow } from '../types';
import { fmtMoney } from '../lib/format';
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

export function CategoryBreakdown({ rows }: { rows: CategoryTotalRow[] }) {
  const expenses = rows.filter(r => r.kind === 'expense' && r.total_cents !== 0)
    .map(r => ({ ...r, value: Math.abs(r.total_cents) / 100 }))
    .sort((a, b) => b.value - a.value);

  if (expenses.length === 0) {
    return (
      <Section title="Expenses by category">
        <div className="text-sm text-slate-500 py-4">
          No expenses for this date yet.
        </div>
      </Section>
    );
  }

  const top = expenses.slice(0, 8);

  return (
    <Section title="Expenses by category">
      <div className="h-56 w-full">
        <ResponsiveContainer>
          <BarChart data={top} layout="vertical" margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <XAxis type="number" tickFormatter={(v: number) => `$${v.toLocaleString()}`} tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(v: number) => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
            <Bar dataKey="value" radius={[0, 6, 6, 0]}>
              {top.map((_, i) => <Cell key={i} fill="#e4572e" />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <table className="w-full text-sm mt-3">
        <tbody>
          {expenses.map(r => (
            <tr key={r.slug} className="border-t border-slate-100">
              <td className="py-2">{r.name}</td>
              <td className="py-2 text-right tnum text-slate-500">{r.tx_count}</td>
              <td className="py-2 text-right tnum font-semibold text-rose-700">
                {fmtMoney(r.total_cents)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
      <h2 className="text-sm font-semibold text-slate-700 mb-3">{title}</h2>
      {children}
    </div>
  );
}
